import type React from 'react';
import { PureComponent, type ReactNode } from 'react';

import classNames from 'classnames';

import { Link } from '../../components/link/link';

import styles from './tabs.module.css';

export type Tab = {
  title: string | ReactNode;
  href?: string;
  route?: string;
  content?: string | ReactNode;
  disabled?: boolean;
};

type Props = {
  // Zero based tab index
  activeTab: number;
  loadAllTabs: boolean;
  tabs: Tab[];
  onTabChange: (index: number) => void;
  justify: boolean;
  className?: string;
  contentClassName?: string;
  tabsClassName?: string;
};

class Tabs extends PureComponent<Props> {
  static defaultProps = {
    activeTab: undefined,
    loadAllTabs: false,
    tabs: [],
    onTabChange(): void {},
    justify: false,
  };

  state = {
    activeTab: 0,
  };

  handleTabChange = ({
    currentTarget,
  }: React.MouseEvent<HTMLElement>): void => {
    const tabIndex = currentTarget.dataset.index;
    this.setState({
      activeTab: Number(tabIndex),
    });
    if (tabIndex !== undefined) {
      this.props.onTabChange(Number(tabIndex));
    }
  };

  render(): ReactNode {
    const {
      tabs,
      activeTab: activeTabProp,
      loadAllTabs,
      justify,
      className,
      contentClassName,
      tabsClassName,
    } = this.props;
    const { activeTab: activeTabState } = this.state;
    let activeTabIndex =
      activeTabProp !== undefined ? activeTabProp : activeTabState;

    if (tabs.length === 0) {
      return null;
    }

    if (activeTabIndex > tabs.length - 1) {
      activeTabIndex = 0;
    }

    if (tabs[activeTabIndex]?.disabled) {
      const firstEnabledIndex = tabs.findIndex((tab) => !tab.disabled);
      if (firstEnabledIndex !== -1) {
        activeTabIndex = firstEnabledIndex;
      }
    }

    return (
      <div className={classNames(className, { [styles.justify]: justify })}>
        <div
          className={classNames(styles.tabs, tabsClassName, {
            [styles.multiple_tabs]: tabs.length > 1,
          })}
          role="tablist"
        >
          {tabs.length > 1
            ? tabs.map((tab, index) => {
                const { href, title, disabled = false } = tab;
                if (href) {
                  return (
                    <Link
                      key={index}
                      data-index={index}
                      className={classNames(styles.tab, {
                        [styles.active]: activeTabIndex === index,
                        [styles.disabled]: disabled,
                      })}
                      to={href}
                      onClick={this.handleTabChange}
                      role="tab"
                      id={`tab-${index}`}
                      aria-controls={`content-${index}`}
                      aria-selected={activeTabIndex === index}
                    >
                      {title}
                    </Link>
                  );
                }
                return (
                  <button
                    key={index}
                    data-index={index}
                    className={classNames(styles.tab, {
                      [styles.active]: activeTabIndex === index,
                      [styles.disabled]: disabled,
                    })}
                    onClick={!disabled ? this.handleTabChange : undefined}
                    type="button"
                    role="tab"
                    id={`tab-${index}`}
                    aria-controls={`content-${index}`}
                    aria-selected={activeTabIndex === index}
                  >
                    {title}
                  </button>
                );
              })
            : undefined}
        </div>
        {loadAllTabs
          ? tabs.map((tab, index) => {
              if (!tab.content) {
                return null;
              }
              return (
                <div
                  className={classNames(styles.tab_content, contentClassName, {
                    [styles.active]: activeTabIndex === index,
                  })}
                  id={`content-${index}`}
                  key={`content-${index}`}
                  role="tabpanel"
                  aria-labelledby={`tab-${index}`}
                >
                  {tab.content}
                </div>
              );
            })
          : tabs[activeTabIndex]?.content && (
              <div
                className={classNames(
                  styles.tab_content,
                  contentClassName,
                  styles.active,
                )}
                id={`content-${activeTabIndex}`}
                role="tabpanel"
                aria-labelledby={`tab-${activeTabIndex}`}
              >
                {tabs[activeTabIndex].content}
              </div>
            )}
      </div>
    );
  }
}

export default Tabs;
